import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Page, type PageTemplate } from "../lib/api";
import { navigate } from "../lib/route";
import { slugify, cleanSlugInput } from "../lib/slug";
import { PageTemplatePreview } from "../components/PageTemplatePreview";

const TEMPLATE_ICONS: Record<string, string> = {
  blank: "📄",
  "about-story": "🙋",
  "services-pricing": "💼",
  process: "🔄",
  testimonials: "⭐",
};


export function PagesPage() {
  const { t } = useTranslation();
  const [pages, setPages] = useState<Page[]>([]);
  const [templates, setTemplates] = useState<PageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [templateID, setTemplateID] = useState("blank");
  const [creating, setCreating] = useState(false);
  const [deletingID, setDeletingID] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const [cloningID, setCloningID] = useState<string | null>(null);

  async function handleClone(id: string) {
    setCloningID(id);
    setError(null);
    try {
      const clone = await api.clonePage(id);
      navigate(`/pages/${clone.ID}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to duplicate page");
    } finally {
      setCloningID(null);
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [list, tmpl] = await Promise.all([
        api.listPages(),
        api.listPageTemplates(),
      ]);
      setPages(list);
      setTemplates(tmpl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function reorder(fromIndex: number, toIndex: number) {
    if (moving) return;
    setMoving(true);
    const reordered = [...pages];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    try {
      // Assign sequential sort_orders from the new positions — works even
      // when all existing sort_orders are 0 (swapping equal values is a no-op).
      await Promise.all(reordered.map((p, i) => api.updatePage(p.ID, { sort_order: i })));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reorder");
    } finally {
      setMoving(false);
    }
  }

  function moveUp(index: number) { if (index > 0) void reorder(index, index - 1); }
  function moveDown(index: number) { if (index < pages.length - 1) void reorder(index, index + 1); }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const finalSlug = slug.trim() || slugify(title);
    setCreating(true);
    setError(null);
    try {
      const page = await api.createPage({
        slug: finalSlug,
        title: title.trim(),
        template_id: templateID || undefined,
      });
      setTitle("");
      setSlug("");
      setSlugEdited(false);
      setTemplateID("blank");
      setShowNew(false);
      navigate(`/pages/${page.ID}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create page");
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Delete this page? This cannot be undone.")) return;
    setDeletingID(id);
    try {
      await api.deletePage(id);
      setPages((prev) => prev.filter((p) => p.ID !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete page");
    } finally {
      setDeletingID(null);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <h1 style={{ margin: 0 }}>{t("pages.title")}</h1>
        <button className="btn-primary" onClick={() => setShowNew((v) => !v)}>
          {showNew ? t("pages.cancel") : t("pages.new_page")}
        </button>
      </div>

      {showNew && (
        <form onSubmit={handleCreate} className="card" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ marginTop: 0 }}>{t("pages.new_title")}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            <div className="form-group">
              <label>{t("pages.title_label")}</label>
              <input
                required
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!slugEdited) setSlug(slugify(e.target.value));
                }}
                placeholder="About me"
              />
            </div>
            <div className="form-group">
              <label>{t("pages.slug_label")}</label>
              <input
                value={slug}
                onChange={(e) => { setSlugEdited(true); setSlug(cleanSlugInput(e.target.value)); }}
                placeholder="about-me"
              />
            </div>
          </div>

          <div style={{ marginTop: "1rem" }}>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500 }}>{t("pages.template_label")}</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "0.75rem" }}>
              {templates.map((t) => (
                <button
                  key={t.ID}
                  type="button"
                  onClick={() => setTemplateID(t.ID)}
                  style={{
                    border: `2px solid ${templateID === t.ID ? "var(--accent)" : "var(--border)"}`,
                    borderRadius: "0.5rem",
                    padding: "0.5rem",
                    background: templateID === t.ID ? "color-mix(in srgb, var(--accent) 10%, transparent)" : "var(--card)",
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex", flexDirection: "column", gap: "0.5rem",
                  }}
                >
                  <PageTemplatePreview template={t} height={110} />
                  <div style={{ padding: "0 0.25rem" }}>
                    <div style={{ fontWeight: 600, marginBottom: "0.15rem" }}>
                      {TEMPLATE_ICONS[t.ID] ? `${TEMPLATE_ICONS[t.ID]} ` : ""}{t.Name}
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{t.Description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={creating} style={{ marginTop: "1rem" }}>
            {creating ? t("pages.creating") : t("pages.create")}
          </button>
        </form>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      ) : pages.length === 0 ? (
        <div className="card">
          <p style={{ marginTop: 0, color: "var(--muted)" }}>
            {t("pages.empty_text")}
          </p>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {[
              { label: t("pages.quick_services"), templateID: "services-pricing", title: "Services & Pricing", slug: "services" },
              { label: t("pages.quick_story"), templateID: "about-story", title: "About", slug: "about" },
              { label: t("pages.quick_faq"), templateID: "blank", title: "FAQs", slug: "faqs" },
            ].map((qs) => (
              <button
                key={qs.templateID + qs.slug}
                className="btn-ghost"
                style={{ fontSize: "0.85rem" }}
                onClick={async () => {
                  setCreating(true);
                  setError(null);
                  try {
                    const page = await api.createPage({ slug: qs.slug, title: qs.title, template_id: qs.templateID });
                    navigate(`/pages/${page.ID}`);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : "Failed to create page");
                  } finally {
                    setCreating(false);
                  }
                }}
                disabled={creating}
              >
                {qs.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {pages.map((p, i) => (
              <div
                key={p.ID}
                className="card"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}
              >
                <div style={{ display: "flex", gap: ".25rem", flexShrink: 0 }}>
                  <button
                    onClick={() => void moveUp(i)}
                    disabled={i === 0 || moving}
                    className="btn-ghost"
                    style={{ padding: ".2rem .5rem", fontSize: ".8rem", opacity: i === 0 ? .3 : 1 }}
                    title="Move up"
                  >↑</button>
                  <button
                    onClick={() => void moveDown(i)}
                    disabled={i === pages.length - 1 || moving}
                    className="btn-ghost"
                    style={{ padding: ".2rem .5rem", fontSize: ".8rem", opacity: i === pages.length - 1 ? .3 : 1 }}
                    title="Move down"
                  >↓</button>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", flexWrap: "wrap" }}>
                    <strong>{p.Title}</strong>
                    <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}>/{p.Slug}</span>
                    {p.IsPublished && !p.HasDraft && (
                      <span style={{ background: "#22c55e", color: "#fff", fontSize: "0.72rem", fontWeight: 600, padding: "0.15rem 0.45rem", borderRadius: "0.3rem" }}>{t("pages.status_live")}</span>
                    )}
                    {p.IsPublished && p.HasDraft && (
                      <span style={{ background: "#f59e0b", color: "#fff", fontSize: "0.72rem", fontWeight: 600, padding: "0.15rem 0.45rem", borderRadius: "0.3rem" }}>{t("pages.status_pending")}</span>
                    )}
                    {!p.IsPublished && (
                      <span style={{ background: "#94a3b8", color: "#fff", fontSize: "0.72rem", fontWeight: 600, padding: "0.15rem 0.45rem", borderRadius: "0.3rem" }}>{t("pages.status_draft")}</span>
                    )}
                  </div>
                  <div style={{ color: "var(--muted)", fontSize: "0.85rem", marginTop: "0.2rem" }}>
                    {p.Blocks?.length ?? 0} block{(p.Blocks?.length ?? 0) === 1 ? "" : "s"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.35rem", flexShrink: 0, flexWrap: "wrap" }}>
                  <a
                    href={`#/pages/${p.ID}`}
                    className="btn-primary"
                    style={{ padding: "0.35rem 0.6rem", fontSize: "0.85rem", textDecoration: "none" }}
                  >
                    {t("common.edit")}
                  </a>
                  <button
                    className="btn-ghost"
                    onClick={() => void handleClone(p.ID)}
                    disabled={cloningID === p.ID}
                    style={{ padding: "0.35rem 0.6rem", fontSize: "0.85rem" }}
                    title="Duplicate this page as a new draft"
                  >
                    {cloningID === p.ID ? "…" : t("common.duplicate")}
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() => void handleDelete(p.ID)}
                    disabled={deletingID === p.ID}
                    style={{ color: "#ef4444", padding: "0.35rem 0.6rem", fontSize: "0.85rem" }}
                  >
                    {deletingID === p.ID ? "…" : t("pages.delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p style={{ color: "var(--muted)", fontSize: ".8rem", marginTop: ".5rem" }}>
            {t("pages.hint")}
          </p>
        </>
      )}
    </div>
  );
}
